import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { searchAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search as SearchIcon,
  Calendar,
  FileText,
  ListTodo,
  ArrowRight,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { format } from 'date-fns';

const Search = () => {
  const { t } = useApp();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const data = await searchAPI.search(query.trim());
      setResults(data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight" data-testid="search-title">
            {t('search.title')}
          </h1>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.placeholder')}
              className="pl-12 pr-24 h-14 rounded-xl text-lg"
              data-testid="search-input"
            />
            <Button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg bg-violet-600 hover:bg-violet-700"
              disabled={loading || !query.trim()}
              data-testid="search-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
            </Button>
          </div>
        </form>

        {/* Results */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="rounded-2xl">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : hasSearched ? (
          results.length > 0 ? (
            <div className="space-y-4" data-testid="search-results">
              <p className="text-sm text-muted-foreground mb-4">
                {results.length} {t('search.resultsCount')}
              </p>
              {results.map((meeting) => (
                <Link key={meeting.id} to={`/meeting/${meeting.id}`} data-testid={`search-result-${meeting.id}`}>
                  <Card className="rounded-2xl border-slate-200 dark:border-slate-800 hover:shadow-lg hover:border-violet-500/50 transition-all cursor-pointer group">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                          {meeting.title}
                        </CardTitle>
                        <Badge variant={meeting.status === 'completed' ? 'default' : 'secondary'}>
                          {meeting.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(meeting.created_at), 'PPP')}
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {meeting.transcript?.length || 0} segments
                        </div>
                        <div className="flex items-center gap-1">
                          <ListTodo className="w-4 h-4" />
                          {meeting.action_items?.length || 0} actions
                        </div>
                      </div>
                      {meeting.summary && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                          {meeting.summary}
                        </p>
                      )}
                      <div className="flex items-center gap-1 text-sm font-medium text-violet-600 dark:text-violet-400">
                        View details
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="rounded-2xl text-center py-12" data-testid="no-results">
              <CardContent>
                <SearchIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">{t('search.noResults')}</h3>
                <p className="text-muted-foreground">{t('search.noResultsDesc')}</p>
              </CardContent>
            </Card>
          )
        ) : (
          <Card className="rounded-2xl text-center py-12 border-dashed" data-testid="search-prompt">
            <CardContent>
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-violet-500 opacity-50" />
              <h3 className="text-lg font-semibold mb-2">Search your meetings</h3>
              <p className="text-muted-foreground">
                Search by title, description, transcript content, or summary
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Search;
